import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type TransitionEvent } from "react";
import { createPortal } from "react-dom";
import { Appear } from "./fade";
import { KanbanLink } from "./KanbanSync";
import { PropertyButton } from "./PropertyDesk";
import { watchKanbanLink } from "./kanbanLink";
import { FicheMark, warmFicheLogos } from "./FicheMark";
import { SpaceBoot } from "./SpaceBoot";
import type { CrmFiche } from "./types";
import { importanceColor, listProperties, taskBadges, type TaskProperty } from "./tasks";
import { blankWeekTask, layoutAfterMove, loadWeekTasks, orderRank, persistWeekMove, shareCaption, shareTone, taskClock, taskTimeLabel, taskDay, watchWeek, isCrmAgendaMirror, agendaMirrorAccent, kanbanBoardName, type WeekTask } from "./week";
import { TaskSheet } from "./WeekBoard";

const WEEKDAYS = ["lun", "mar", "mer", "jeu", "ven", "sam", "dim"];

export function MonthBoard({ fiches, onOpenFiche }: { fiches: CrmFiche[]; onOpenFiche: (id: number) => void }) {
  const [cursor, setCursor] = useState(() => monthStart(new Date()));
  const [tasks, setTasks] = useState<WeekTask[]>([]);
  const [ready, setReady] = useState(false);
  const [catalog, setCatalog] = useState<TaskProperty[]>([]);
  const [error, setError] = useState("");
  const [over, setOver] = useState("");
  const [ghost, setGhost] = useState<DragGhost | null>(null);
  const ghostRef = useRef<DragGhost | null>(null);
  const slotRef = useRef<DropSlot | null>(null);
  const [open, setOpen] = useState<WeekTask | null>(null);
  const [slide, setSlide] = useState<Slide | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const lock = useRef(false);
  const painted = useRef(false);
  const boardGen = useRef(0);
  const fichesRef = useRef(fiches);
  fichesRef.current = fiches;
  const today = iso(new Date());
  const title = slide?.to ?? cursor;

  async function reload() {
    const mine = ++boardGen.current;
    const rows = await loadWeekTasks();
    if (mine !== boardGen.current) return;
    setTasks(rows);
  }

  async function refreshBoard() {
    if (refreshing) return;
    setRefreshing(true);
    setError("");
    try {
      const [rows, props] = await Promise.all([loadWeekTasks(), listProperties()]);
      setTasks(rows);
      setCatalog(props);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Actualisation impossible.");
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    let alive = true;
    const refresh = (blank: boolean) => {
      const mine = ++boardGen.current;
      if (blank) setReady(false);
      void Promise.all([loadWeekTasks(), listProperties()])
        .then(async ([rows, props]) => {
          if (!alive || mine !== boardGen.current) return;
          if (!painted.current) await warmFicheLogos(fichesRef.current.map((fiche) => fiche.logo_path));
          if (!alive || mine !== boardGen.current) return;
          setTasks(rows);
          setCatalog(props);
          painted.current = true;
        })
        .catch((reason: unknown) => { if (alive) setError(reason instanceof Error ? reason.message : "Mois indisponible."); })
        .finally(() => { if (alive) setReady(true); });
    };
    refresh(true);
    const stop = watchKanbanLink(() => refresh(true));
    const stopWeek = watchWeek(() => refresh(false));
    return () => { alive = false; stop(); stopWeek(); };
  }, []);

  function grab(task: WeekTask, event: ReactPointerEvent<HTMLElement>) {
    if (event.button !== 0) return;
    const card = event.currentTarget;
    const startX = event.clientX;
    const startY = event.clientY;
    const width = card.offsetWidth;
    let moved = false;
    const move = (pointer: PointerEvent) => {
      if (!moved && Math.hypot(pointer.clientX - startX, pointer.clientY - startY) < 5) return;
      if (!moved) {
        moved = true;
        document.body.classList.add("crm-grabbing");
      }
      pointer.preventDefault();
      const next = { id: task.id, title: task.title, x: pointer.clientX, y: pointer.clientY, width };
      ghostRef.current = next;
      setGhost(next);
      const raw = dayUnder(pointer.clientX, pointer.clientY, task.id);
      slotRef.current = raw;
      setOver(raw?.day || "");
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.classList.remove("crm-grabbing");
      const chosen = slotRef.current;
      const id = ghostRef.current?.id || "";
      ghostRef.current = null;
      slotRef.current = null;
      setGhost(null);
      setOver("");
      if (!moved || !id || !chosen) return;
      const stopClick = (native: Event) => {
        native.preventDefault();
        native.stopPropagation();
      };
      window.addEventListener("click", stopClick, true);
      window.setTimeout(() => window.removeEventListener("click", stopClick, true), 0);
      void place(id, chosen.day, chosen.index);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  async function place(id: string, day: string, index: number) {
    const next = layoutAfterMove(tasks, id, day, index);
    if (!next) return;
    boardGen.current += 1;
    setTasks(next);
    setError("");
    try {
      await persistWeekMove(tasks, next);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Déplacement impossible.");
      await reload().catch(() => undefined);
    }
  }

  function turn(delta: number) {
    if (delta === 0 || lock.current) return;
    const to = addMonths(cursor, delta);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setCursor(to);
      return;
    }
    lock.current = true;
    const next: Slide = { dir: delta > 0 ? 1 : -1, from: cursor, to, go: false };
    setSlide(next);
    requestAnimationFrame(() => requestAnimationFrame(() => setSlide({ ...next, go: true })));
  }

  function settled(event: TransitionEvent<HTMLDivElement>) {
    if (event.propertyName !== "transform" || event.target !== event.currentTarget || !slide?.go) return;
    setCursor(slide.to);
    setSlide(null);
    lock.current = false;
  }

  return (
    <section className="flex h-[calc(100dvh-5.5rem)] min-h-0 flex-col px-4 py-4">
      {!ready ? (
        <SpaceBoot />
      ) : (
        <Appear className="flex min-h-0 flex-1 flex-col">
          <header className="mb-3 flex shrink-0 flex-wrap items-center gap-2">
            <h2 className="crm-title min-w-0 text-lg font-semibold capitalize tracking-tight">{title.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</h2>
            <button type="button" className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 text-slate-500" aria-label="Mois précédent" onClick={() => turn(-1)}>‹</button>
            <button type="button" className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 text-slate-500" aria-label="Mois suivant" onClick={() => turn(1)}>›</button>
            <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold" onClick={() => turn(monthSpan(cursor, monthStart(new Date())))}>Aujourd’hui</button>
            <PropertyButton />
            <button
              type="button"
              className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500 disabled:opacity-50"
              aria-label="Actualiser"
              title="Actualiser"
              disabled={refreshing}
              onClick={() => void refreshBoard()}
            >
              {refreshing ? <span className="crm-browse-loading-spin !h-3.5 !w-3.5" aria-hidden /> : <span className="material-icons-round text-[16px] leading-none">refresh</span>}
            </button>
            <KanbanLink />
          </header>
          {error ? <p className="mb-2 shrink-0 text-sm text-rose-700">{error}</p> : null}
          <div className="mb-1 grid shrink-0 grid-cols-7 gap-1 px-1 text-center text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
            {WEEKDAYS.map((label) => <span key={label}>{label}</span>)}
          </div>
          <div className="relative min-h-0 flex-1 overflow-hidden">
            {slide ? (
              <div
                className="crm-month-track flex h-full w-[200%]"
                style={{ transform: `translate3d(${slide.dir === 1 ? (slide.go ? "-50%" : "0") : (slide.go ? "0" : "-50%")}, 0, 0)` }}
                onTransitionEnd={settled}
              >
                {(slide.dir === 1 ? [slide.from, slide.to] : [slide.to, slide.from]).map((date) => (
                  <div key={iso(date)} className="h-full w-1/2 shrink-0">
                    <MonthGrid cursor={date} tasks={tasks} fiches={fiches} catalog={catalog} today={today} hot="" onOpen={setOpen} onCreate={(day) => setOpen(blankWeekTask(day))} onGrab={grab} />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <MonthGrid cursor={cursor} tasks={tasks} fiches={fiches} catalog={catalog} today={today} hot={over} onOpen={setOpen} onCreate={(day) => setOpen(blankWeekTask(day))} onGrab={grab} />
                {ghost ? createPortal(
                  <div
                    className="crm-task-ghost pointer-events-none fixed z-[80] rounded-lg border border-slate-200 bg-white px-2 py-1 text-left text-xs shadow-[0_18px_40px_-18px_rgba(15,23,42,0.55)]"
                    style={{ width: ghost.width, left: ghost.x + 12, top: ghost.y + 8 }}
                  >
                    {ghost.title}
                  </div>,
                  document.body,
                ) : null}
              </>
            )}
          </div>
        </Appear>
      )}
      {open ? (
        <TaskSheet
          task={open}
          fiches={fiches}
          onClose={() => setOpen(null)}
          onOpenFiche={onOpenFiche}
          onSaved={async () => { setOpen(null); await reload(); }}
        />
      ) : null}
    </section>
  );
}

function MonthGrid({
  cursor, tasks, fiches, catalog, today, hot, onOpen, onCreate, onGrab,
}: {
  cursor: Date;
  tasks: WeekTask[];
  fiches: CrmFiche[];
  catalog: TaskProperty[];
  today: string;
  hot: string;
  onOpen: (task: WeekTask) => void;
  onCreate: (day: string) => void;
  onGrab: (task: WeekTask, event: ReactPointerEvent<HTMLElement>) => void;
}) {
  return (
    <div className="grid h-full grid-cols-7 grid-rows-6 gap-1">
      {monthCells(cursor).map((day) => {
        const key = iso(day);
        const rows = tasksOf(tasks, key);
        const inMonth = day.getMonth() === cursor.getMonth();
        return (
          <div
            key={key}
            data-day={key}
            role="button"
            tabIndex={0}
            className={inMonth ? "crm-surface flex min-h-0 cursor-pointer flex-col rounded-xl border border-slate-200 p-1 transition hover:border-slate-300" : "crm-surface flex min-h-0 cursor-pointer flex-col rounded-xl border border-slate-200 p-1 opacity-45 transition hover:opacity-70"}
            style={{
              ...(key === today ? { boxShadow: "inset 0 3px 0 var(--crm-accent, #3b82f6)" } : {}),
              ...(hot === key ? { outline: "2px solid #0f172a", outlineOffset: "1px" } : {}),
            }}
            onClick={() => onCreate(key)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onCreate(key);
              }
            }}
          >
            <p className="px-1 text-xs font-semibold text-slate-500">{day.getDate()}</p>
            <ul className="crm-day-scroll min-h-0 flex-1 space-y-1 overflow-auto">
              {rows.map((task) => {
                const fiche = ficheOf(fiches, task);
                const tint = importanceColor(task.meta, catalog);
                const badges = taskBadges(task.meta, catalog);
                const fromAgenda = isCrmAgendaMirror(task);
                const accent = fromAgenda ? agendaMirrorAccent(task) : "";
                return (
                  <li key={task.id}>
                    <button
                      type="button"
                      data-task-id={task.id}
                      onPointerDown={(event) => onGrab(task, event)}
                      className={
                        task.status === "done"
                          ? "crm-task-card is-done w-full rounded-lg border border-transparent px-1.5 py-1 text-left"
                          : fromAgenda
                            ? "crm-task-card is-agenda w-full rounded-lg border border-slate-200/80 px-1.5 py-1 text-left"
                            : "crm-task-card w-full rounded-lg px-1.5 py-1 text-left"
                      }
                      style={
                        task.status === "done"
                          ? { borderLeftWidth: 3 }
                          : fromAgenda
                            ? ({ ["--agenda-mirror-accent"]: accent } as CSSProperties)
                            : tint
                              ? { borderLeftWidth: 3, borderLeftColor: tint }
                              : undefined
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpen(task);
                      }}
                    >
                      <span className="flex items-center gap-1">
                        {fromAgenda ? <span className="material-icons-round shrink-0 text-[11px]" style={{ fontSize: 11, width: 11, height: 11, color: accent }}>event</span> : null}
                        {kanbanBoardName(task.meta) ? <span className="crm-task-from-kanban shrink-0 rounded px-1 text-[9px] font-medium" title={`Envoyée depuis le kanban ${kanbanBoardName(task.meta)}`}>Kanban</span> : null}
                        {fiche ? <FicheMark path={fiche.logo_path} name={fiche.name} bare={fiche.scope === "interne"} className="h-4 w-4 text-[8px]" /> : null}
                        <span className={task.status === "done" ? "crm-task-title min-w-0 flex-1 truncate text-[11px] font-normal line-through" : "min-w-0 flex-1 truncate text-[11px] font-normal"}>
                          {taskTimeLabel(task) ? <span className="mr-1 text-slate-400">{taskTimeLabel(task)}</span> : null}
                          {task.title}
                        </span>
                      </span>
                      {shareCaption(task) ? <span className={shareTone(task) === "in" ? "mt-0.5 block truncate text-[9px] font-medium text-violet-700" : "mt-0.5 block truncate text-[9px] font-medium text-sky-700"}>{shareCaption(task)}</span> : null}
                      {badges.length > 0 ? (
                        <span className="crm-task-badges mt-1 flex flex-wrap gap-0.5">
                          {badges.map((badge) => (
                            <span key={badge.key} className="rounded px-1 py-px text-[9px] font-medium" style={{ background: badge.bg, color: badge.text }}>{badge.label}</span>
                          ))}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

type Slide = { dir: 1 | -1; from: Date; to: Date; go: boolean };
type DragGhost = { id: string; title: string; x: number; y: number; width: number };
type DropSlot = { day: string; index: number };

function dayUnder(x: number, y: number, dragId: string): DropSlot | null {
  const node = document.elementFromPoint(x, y);
  const column = node?.closest("[data-day]");
  const day = column?.getAttribute("data-day") || "";
  if (!day) return null;
  const cards = [...(column?.querySelectorAll<HTMLElement>("[data-task-id]") || [])].filter((card) => card.dataset.taskId !== dragId);
  for (let index = 0; index < cards.length; index += 1) {
    const rect = cards[index].getBoundingClientRect();
    if (y < rect.top + rect.height / 2) return { day, index };
  }
  return { day, index: cards.length };
}

function tasksOf(tasks: WeekTask[], key: string): WeekTask[] {
  return tasks
    .filter((task) => taskDay(task) === key)
    .sort((a, b) => {
      const done = Number(a.status === "done") - Number(b.status === "done");
      if (done) return done;
      const aOrder = orderRank(a, key);
      const bOrder = orderRank(b, key);
      if (aOrder !== bOrder) {
        if (aOrder === 999999) return 1;
        if (bOrder === 999999) return -1;
        return aOrder - bOrder;
      }
      return taskClock(a).localeCompare(taskClock(b)) || a.title.localeCompare(b.title, "fr");
    });
}

function ficheOf(fiches: CrmFiche[], task: WeekTask): CrmFiche | undefined {
  const id = Number(task.meta.crmFicheId);
  if (id > 0) {
    const row = fiches.find((fiche) => fiche.id === id);
    if (row) return row;
  }
  const key = task.client_name.trim().toLocaleLowerCase("fr");
  if (!key) return undefined;
  return fiches.find((row) => row.name.trim().toLocaleLowerCase("fr") === key);
}

function monthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, count: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + count, 1);
}

function monthSpan(from: Date, to: Date): number {
  return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
}

function monthCells(cursor: Date): Date[] {
  const first = monthStart(cursor);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(1 - offset);
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function iso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}
